<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";
import {
  isJust,
  just,
  nothing,
  toDomAttr,
  toMaybe,
  type Maybe,
} from "../../lib/maybe";
import ZwField from "./ZwField.vue";
import { usePaintedSelection } from "./usePaintedSelection";

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ default: "" });

const props = withDefaults(
  defineProps<{
    /** Callers pass `just(text)` or `nothing` explicitly. */
    label: Maybe<string>;
    id: Maybe<string>;
    rows?: number;
    /** Grow/shrink height to fit content. */
    autosize?: boolean;
    /** `plain` = borderless inline edit (e.g. comment bodies). */
    variant?: "default" | "plain";
  }>(),
  {
    rows: 3,
    autosize: false,
    variant: "default",
  },
);

const emit = defineEmits<{
  autosize: [];
}>();

const attrs = useAttrs();
const input = useTemplateRef<HTMLElement>("input");
const face = useTemplateRef<HTMLElement>("face");
const inputEl = computed(() => toMaybe(input.value));
const { selectionRects, refreshSelectionPaint } = usePaintedSelection(inputEl);

const selecting = computed(() => selectionRects.value.length > 0);
/** Local mirrors so placeholder chrome clears even when parent v-model does not round-trip. */
const viewText = ref(model.value === "" ? "" : model.value);
const empty = ref(viewText.value === "");
let resizeObserver: Maybe<ResizeObserver> = nothing;
let measuredWidth = -1;

const placeholder = computed(() => {
  const value = attrs.placeholder;
  return typeof value === "string" ? just(value) : nothing;
});

const disabled = computed(
  () => "disabled" in attrs && attrs.disabled !== false,
);
const readonly = computed(
  () => "readonly" in attrs && attrs.readonly !== false,
);

const editMode = computed(() =>
  disabled.value || readonly.value ? "false" : "plaintext-only",
);

const forwarded = computed(() => {
  const next: Record<string, unknown> = { ...attrs };
  delete next.placeholder;
  delete next.disabled;
  delete next.readonly;
  delete next.writingsuggestions;
  delete next.autocorrect;
  delete next.autocapitalize;
  delete next.class;
  return next;
});

const shellClass = computed(() => attrs.class);

function readText(el: HTMLElement): string {
  if (el.childNodes.length === 1 && el.firstChild instanceof HTMLBRElement) {
    return "";
  }
  // Walk DOM so CSS ::before/::after never leak in (innerText can include them).
  const pieces: string[] = [];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      pieces.push(node.textContent ?? "");
      return;
    }
    if (node.nodeName === "BR") {
      pieces.push("\n");
      return;
    }
    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }
  };
  walk(el);
  return pieces.join("").replace(/\r\n/g, "\n").replace(/\n$/, "");
}

function writeText(el: HTMLElement, value: string): void {
  const next = value === "" ? "" : value;
  if (readText(el) === next) return;
  el.textContent = next;
}

function commitText(text: string): void {
  viewText.value = text;
  empty.value = text === "";
  model.value = text;
}

function syncFaceScroll(): void {
  const el = input.value;
  const faceEl = face.value;
  if (el === null || faceEl === null) return;
  faceEl.scrollTop = el.scrollTop;
  faceEl.scrollLeft = el.scrollLeft;
}

function syncHeight(): void {
  const el = input.value;
  if (props.autosize === false || el === null) return;
  el.style.height = "0px";
  el.style.height = `${Math.max(el.scrollHeight, 1)}px`;
  const faceEl = face.value;
  if (faceEl !== null) faceEl.style.height = el.style.height;
  emit("autosize");
  refreshSelectionPaint();
}

function onInput(): void {
  const el = input.value;
  if (el === null) return;
  commitText(readText(el));
  if (props.autosize) syncHeight();
  else refreshSelectionPaint();
}

function onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const text = event.clipboardData?.getData("text/plain") ?? "";
  document.execCommand("insertText", false, text);
}

watch(model, (value) => {
  const normalized = value === "" ? "" : value;
  viewText.value = normalized;
  empty.value = normalized === "";
  const el = input.value;
  if (el === null) return;
  if (document.activeElement === el && readText(el) === normalized) {
    void nextTick(syncHeight);
    return;
  }
  writeText(el, normalized);
  void nextTick(syncHeight);
});

onMounted(() => {
  const initial = model.value === "" ? "" : model.value;
  viewText.value = initial;
  empty.value = initial === "";
  const el = input.value;
  if (el !== null) {
    writeText(el, initial);
    if (props.autosize && typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        const width = el.getBoundingClientRect().width;
        if (width === measuredWidth) return;
        measuredWidth = width;
        requestAnimationFrame(() => {
          if (el.isConnected) syncHeight();
        });
      });
      observer.observe(el);
      resizeObserver = just(observer);
    }
  }
  void nextTick(syncHeight);
  void document.fonts?.ready.then(() => {
    if (input.value !== null) syncHeight();
  });
});

onBeforeUnmount(() => {
  if (isJust(resizeObserver)) resizeObserver.value.disconnect();
});

defineExpose({
  focus: (options?: FocusOptions) => input.value?.focus(options),
  el: input,
  autosize: syncHeight,
});
</script>

<template>
  <ZwField v-if="isJust(label)" :label="label.value" :for-id="id">
    <div
      class="zw-input-shell zw-textarea-shell"
      :class="[
        shellClass,
        {
          'zw-input-empty': empty,
          'zw-input-selecting': selecting,
          'zw-textarea-plain-shell': variant === 'plain',
        },
      ]"
    >
      <div ref="face" class="zw-input-face" aria-hidden="true">
        <div class="zw-input-sel-layer">
          <div
            v-for="(r, i) in selectionRects"
            :key="i"
            class="zw-input-sel"
            :style="{
              top: `${r.top}px`,
              left: `${r.left}px`,
              width: `${r.width}px`,
              height: `${r.height}px`,
            }"
          />
        </div>
        <div class="zw-input-face-text">{{ viewText }}</div>
      </div>
      <div
        :id="toDomAttr(id)"
        ref="input"
        class="zw-input zw-textarea"
        :class="{
          'zw-textarea-autosize': autosize,
          'zw-textarea-plain': variant === 'plain',
        }"
        role="textbox"
        :aria-label="toDomAttr(label)"
        :contenteditable="editMode"
        :aria-multiline="true"
        :aria-disabled="disabled || undefined"
        :aria-readonly="readonly || undefined"
        :aria-placeholder="toDomAttr(placeholder)"
        :data-placeholder="toDomAttr(placeholder)"
        :data-rows="rows"
        :tabindex="disabled ? -1 : 0"
        v-bind="forwarded"
        writingsuggestions="false"
        autocorrect="off"
        autocapitalize="off"
        @input="onInput"
        @paste="onPaste"
        @keyup="refreshSelectionPaint"
        @mouseup="refreshSelectionPaint"
        @scroll="
          syncFaceScroll();
          refreshSelectionPaint();
        "
      />
    </div>
  </ZwField>
  <div
    v-else
    class="zw-input-shell zw-textarea-shell"
    :class="[
      shellClass,
      {
        'zw-input-empty': empty,
        'zw-input-selecting': selecting,
        'zw-textarea-plain-shell': variant === 'plain',
      },
    ]"
  >
    <div ref="face" class="zw-input-face" aria-hidden="true">
      <div class="zw-input-sel-layer">
        <div
          v-for="(r, i) in selectionRects"
          :key="i"
          class="zw-input-sel"
          :style="{
            top: `${r.top}px`,
            left: `${r.left}px`,
            width: `${r.width}px`,
            height: `${r.height}px`,
          }"
        />
      </div>
      <div class="zw-input-face-text">{{ viewText }}</div>
    </div>
    <div
      :id="toDomAttr(id)"
      ref="input"
      class="zw-input zw-textarea"
      :class="{
        'zw-textarea-autosize': autosize,
        'zw-textarea-plain': variant === 'plain',
      }"
      role="textbox"
      :aria-label="toDomAttr(label)"
      :contenteditable="editMode"
      :aria-multiline="true"
      :aria-disabled="disabled || undefined"
      :aria-readonly="readonly || undefined"
      :aria-placeholder="toDomAttr(placeholder)"
      :data-placeholder="toDomAttr(placeholder)"
      :data-rows="rows"
      :tabindex="disabled ? -1 : 0"
      v-bind="forwarded"
      writingsuggestions="false"
      autocorrect="off"
      autocapitalize="off"
      @input="onInput"
      @paste="onPaste"
      @keyup="refreshSelectionPaint"
      @mouseup="refreshSelectionPaint"
      @scroll="
        syncFaceScroll();
        refreshSelectionPaint();
      "
    />
  </div>
</template>
